/* eslint-disable no-undef */
import React, { useState, useEffect, useCallback } from 'react';

const PhotoGalleryApp = () => {
  const [isSignup, setIsSignup] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    ghlEmail: '',
    ghlLocationId: '',
    ghlApiKey: ''
  });
  const [formError, setFormError] = useState('');
  const [photographerBranding, setPhotographerBranding] = useState(null);
  const [loadingBranding, setLoadingBranding] = useState(false);
  const [creatingGallery, setCreatingGallery] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginError, setLoginError] = useState(false);
  const [debugLog, setDebugLog] = useState([]);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [showGalleryView, setShowGalleryView] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showBusinessSettings, setShowBusinessSettings] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [ghlContacts, setGhlContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [ghlConfigured, setGhlConfigured] = useState(true);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [editingGalleryName, setEditingGalleryName] = useState(null);
  const [tempGalleryName, setTempGalleryName] = useState('');
  const [showCoverUploadModal, setShowCoverUploadModal] = useState(false);
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [coverPhotoFile, setCoverPhotoFile] = useState(null);
  const [localChanges, setLocalChanges] = useState({});
  const [saveMessage, setSaveMessage] = useState('');
  const [loadingLocationName, setLoadingLocationName] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  
  const [businessSettings, setBusinessSettings] = useState({
  businessName: '',
  logo: null,
  ghlLocationId: '',
  ghlApiKey: ''  // ✅ ADD THIS LINE
});
  
  // Client-specific state
  const [galleryData, setGalleryData] = useState(null);
  const [clientEmail, setClientEmail] = useState('');
  const [galleryPassword, setGalleryPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [clientLoginError, setClientLoginError] = useState('');
  const [selectedPhotosForDownload, setSelectedPhotosForDownload] = useState(new Set());
  const [isDownloading, setIsDownloading] = useState(false);

 const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const addToDebugLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLog(prev => [...prev, `[${timestamp}] ${message}`]);
  };
 // NEW - Add this function here:
  const fetchPhotographerBranding = useCallback(async (galleryId) => {
  try {
    setLoadingBranding(true);
    const response = await fetch(`${API_BASE}/api/public/gallery/${galleryId}/branding`);
    const data = await response.json();
    
    if (data.success) {
      setPhotographerBranding(data.branding);
      addToDebugLog(`Loaded branding for: ${data.branding.businessName}`);
    }
  } catch (error) {
    addToDebugLog('Failed to load photographer branding: ' + error.message);
  } finally {
    setLoadingBranding(false);
  }
}, []); // ✅ Memoize this function
   
useEffect(() => {
  const urlPath = window.location.pathname;
  const galleryMatch = urlPath.match(/\/client-gallery\/([a-zA-Z0-9]+)/);
  
  if (galleryMatch) {
    const galleryId = galleryMatch[1];
    fetchPhotographerBranding(galleryId);
    
    // ✅ Update manifest link to use gallery-specific manifest
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = `${API_BASE}/api/manifest/${galleryId}`;
    }
  }
}, [fetchPhotographerBranding]);

useEffect(() => {
  // Once branding loads, update meta tags
  if (photographerBranding) {
    // Update Apple touch icon
    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (!appleTouchIcon) {
      appleTouchIcon = document.createElement('link');
      appleTouchIcon.rel = 'apple-touch-icon';
      document.head.appendChild(appleTouchIcon);
    }
    
    if (photographerBranding.businessLogo) {
      appleTouchIcon.href = photographerBranding.businessLogo;
    }
    
    // Update Apple app title
    let appleAppTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleAppTitle) {
      appleAppTitle = document.createElement('meta');
      appleAppTitle.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleAppTitle);
    }
    
    if (photographerBranding.businessName) {
      appleAppTitle.setAttribute('content', photographerBranding.businessName);
      document.title = `${photographerBranding.businessName} - Client Gallery`;
    }
  }
}, [photographerBranding]);

  const fetchLocationName = async () => {
    if (!businessSettings.ghlLocationId) return;
    
    try {
      setLoadingLocationName(true);
      addToDebugLog('Fetching location name from Elevate CRM...');
      
      const locationResponse = await fetch(`${API_BASE}/api/ghl/location-info`);
      const locationData = await locationResponse.json();
      
      if (locationData.success && locationData.location?.name) {
        setBusinessSettings(prev => ({ 
          ...prev, 
          businessName: locationData.location.name 
        }));
        addToDebugLog(`Location name set to: ${locationData.location.name}`);
      }
    } catch (error) {
      addToDebugLog(`Failed to fetch location name: ${error.message}`);
    } finally {
      setLoadingLocationName(false);
    }
  };

const saveBusinessSettings = async () => {
  try {
    const response = await fetch(`${API_BASE}/api/profile/settings`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({
        businessName: businessSettings.businessName,
        businessLogo: businessSettings.logo,
             // ✅ ADD THIS
        ghlLocationId: businessSettings.ghlLocationId,
        ghlLocationToken: businessSettings.ghlApiKey // ✅ ADD THIS
      })
    });

    const data = await response.json();
    
    if (data.success) {
      setSaveMessage('Changes Saved');
      addToDebugLog('Business settings saved successfully');
    } else {
      setSaveMessage('Error saving changes');
      addToDebugLog('Failed to save settings: ' + data.error);
    }
    
    setTimeout(() => {
      setSaveMessage('');
    }, 3000);
    
  } catch (error) {
    setSaveMessage('Error saving changes');
    addToDebugLog(`Error saving settings: ${error.message}`);
    
    setTimeout(() => {
      setSaveMessage('');
    }, 3000);
  }
};
  useEffect(() => {
    if (businessSettings.ghlApiKey && businessSettings.ghlLocationId) {
      fetchLocationName();
    }
  }, [businessSettings.ghlApiKey, businessSettings.ghlLocationId]);

  const applyLocalChanges = useCallback((gallery) => {
    const changes = localChanges[gallery.id];
    if (!changes) return gallery;
    
    let updatedGallery = { ...gallery };
    
    if (changes.name) {
      updatedGallery.name = changes.name;
    }
    
    if (changes.coverPhotoId || changes.imageOrder) {
      updatedGallery.images = updatedGallery.images.map(photo => ({
        ...photo,
        isCover: photo.id === changes.coverPhotoId
      }));
      
      if (changes.imageOrder) {
        const orderedImages = changes.imageOrder.map(id => 
          updatedGallery.images.find(img => img.id === id)
        ).filter(Boolean);
        updatedGallery.images = orderedImages;
      }
    }
    
    return updatedGallery;
  }, [localChanges]);
const loadUserSettings = async () => {
  try {
    const response = await fetch(`${API_BASE}/api/profile`, {
      credentials: 'include'
    });
    const data = await response.json();
    
    if (data.success && data.user) {
      setBusinessSettings({
        businessName: data.user.businessName || '',
        logo: data.user.businessLogo || null,
        ghlLocationId: data.user.ghlLocationId || '',
        ghlApiKey: data.user.ghlLocationToken || ''  // ✅ ADD THIS LINE
      });
      
      // ✅ Also set the GHL configured state
      setGhlConfigured(!!data.user.ghlLocationToken && !!data.user.ghlLocationId);
      
      addToDebugLog('User settings loaded');
    }
  } catch (error) {
    addToDebugLog('Failed to load settings: ' + error.message);
  }
};
  const checkAuthStatus = useCallback(async () => {
  // ✅ CRITICAL: Never run photographer auth check on client pages
  const urlPath = window.location.pathname;
  if (urlPath.startsWith('/client-gallery/')) {
    addToDebugLog('Skipping photographer auth check - on client page');
    return;
  }
  
  // ✅ Don't overwrite client authentication
  if (currentUser?.type === 'client') {
    addToDebugLog('Skipping photographer auth check - client already logged in');
    return;
  }
  
  try {
    const response = await fetch(`${API_BASE}/api/auth-status`, {
      credentials: 'include'
    });
    const data = await response.json();
    if (data.authenticated) {
      setCurrentUser({ type: 'photographer', ...data.user });
      loadGalleries();
      loadUserSettings();
      addToDebugLog('User authenticated');
    } else {
      addToDebugLog('User not authenticated');
    }
  } catch (error) {
    addToDebugLog('Auth check failed: ' + error.message);
  }
}, [currentUser]);
useEffect(() => {
  console.log('API_BASE:', API_BASE); // Should show your backend URL
  addToDebugLog(`API configured: ${API_BASE}`);
}, []);
useEffect(() => {
  addToDebugLog('ELEVATE Gallery system initialized');
  
  // Only check photographer auth if NOT on client path
  const urlPath = window.location.pathname;
  const isClientPath = urlPath.includes('/client');
  
  if (!isClientPath) {
    checkAuthStatus();
  }


  


  const handleMessage = (event) => {
    const allowedOrigins = [
      'http://localhost:3001',
      API_BASE
    ];
    
    if (allowedOrigins.includes(event.origin) && event.data.type === 'AUTH_SUCCESS') {
      checkAuthStatus();
      addToDebugLog('OAuth success detected from popup');
    }
  };
  
  window.addEventListener('message', handleMessage);
  return () => window.removeEventListener('message', handleMessage);
}, []); // ✅ EMPTY ARRAY - only run once on mount

  const loadGalleries = async () => {
  try {
    setLoading(true);
    const response = await fetch(`${API_BASE}/api/galleries`, {
      credentials: 'include'  // ✅ ADD THIS
    });
    const data = await response.json();
    if (data.success) {
      setGalleries(data.galleries);
      addToDebugLog(`Loaded ${data.galleries.length} galleries`);
    } else {
      addToDebugLog('Failed to load galleries: ' + (data.error || 'Unknown error'));
    }
  } catch (error) {
    addToDebugLog('Failed to load galleries: ' + error.message);
  } finally {
    setLoading(false);
  }
};
  const loadGallery = async (galleryId) => {
  try {
    const response = await fetch(`${API_BASE}/api/galleries/${galleryId}`, {
      credentials: 'include'  // ✅ ADD THIS
    });
    const data = await response.json();
    if (data.success) {
      return data.gallery;
    }
  } catch (error) {
    addToDebugLog('Failed to load gallery: ' + error.message);
  }
  return null;
};
  const uploadPhotos = async (galleryId, files) => {
    try {
      setUploadProgress(true);
      const formData = new FormData();
      
      for (const file of files) {
        formData.append('photos', file);
      }

      const response = await fetch(`${API_BASE}/api/galleries/${galleryId}/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        addToDebugLog(`Uploaded ${data.images.length} photos`);
        const updatedGallery = await loadGallery(galleryId);
        setSelectedGallery(updatedGallery);
        await loadGalleries();
        return true;
      } else {
        addToDebugLog('Upload failed: ' + data.error);
        return false;
      }
    } catch (error) {
      addToDebugLog('Upload error: ' + error.message);
      return false;
    } finally {
      setUploadProgress(false);
    }
  };

  const fetchGHLContacts = async (searchQuery = '') => {
  if (!searchQuery || searchQuery.length < 2) {
    setGhlContacts([]);
    return;
  }

  setSearchingContacts(true);
  try {
    const response = await fetch(`${API_BASE}/api/ghl/contacts/search?query=${encodeURIComponent(searchQuery)}`, {
      credentials: 'include'
    });
    const data = await response.json();
    
    if (data.needsConfiguration) {
      setGhlConfigured(false);
      setGhlContacts([]);
      addToDebugLog('Elevate CRM not configured');
    } else if (data.success) {
      setGhlConfigured(true);
      setGhlContacts(data.contacts);
      addToDebugLog(`Found ${data.contacts.length} contacts matching "${searchQuery}"`);
    } else {
      setGhlContacts([]);
      addToDebugLog('No contacts found');
    }
  } catch (error) {
    addToDebugLog('GHL API error: ' + error.message);
    setGhlContacts([]);
  } finally {
    setSearchingContacts(false);
  }
};

  const createGalleryFromContact = async (contact) => {
    if (creatingGallery) return;
    try {
      const response = await fetch(`${API_BASE}/api/ghl/create-gallery/${contact.id}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        await loadGalleries();
        setShowAddClientModal(false);
        addToDebugLog(`Gallery created for ${contact.firstName} ${contact.lastName}`);
      } else {
        addToDebugLog('Failed to create gallery from contact: ' + data.error);
      }
    } catch (error) {
      addToDebugLog('Error creating gallery from contact: ' + error.message);
    }
  };

  const deleteGallery = async (galleryId) => {
    try {
      const response = await fetch(`${API_BASE}/api/galleries/${galleryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        await loadGalleries();
        if (selectedGallery?.id === galleryId) {
          setSelectedGallery(null);
        }
        addToDebugLog('Gallery deleted successfully');
      } else {
        addToDebugLog('Failed to delete gallery: ' + data.error);
      }
    } catch (error) {
      addToDebugLog('Error deleting gallery: ' + error.message);
    }
  };

  const deleteSelectedPhotos = async () => {
    if (selectedPhotos.size === 0 || !selectedGallery) return;
    
    try {
      const photoIds = Array.from(selectedPhotos);
      const response = await fetch(`${API_BASE}/api/galleries/${selectedGallery.id}/photos`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ photoIds })
      });
      
      const data = await response.json();
      if (data.success) {
        const updatedGallery = await loadGallery(selectedGallery.id);
        setSelectedGallery(updatedGallery);
        setSelectedPhotos(new Set());
        await loadGalleries();
        addToDebugLog(`Deleted ${photoIds.length} photos`);
      } else {
        addToDebugLog('Failed to delete photos: ' + data.error);
      }
    } catch (error) {
      addToDebugLog('Error deleting photos: ' + error.message);
    }
  };

 const selectGallery = async (gallery) => {
    const fullGallery = await loadGallery(gallery.id);
    const galleryWithLocalChanges = applyLocalChanges(fullGallery);
    
    setSelectedGallery(galleryWithLocalChanges);
    setSelectedClient(gallery.id);
    
    // Clear cover photo state when switching galleries
    setCoverPhoto(null);
    setCoverPhotoFile(null);
  };
const handleForgotPassword = async () => {
  if (!forgotPasswordEmail.trim()) {
    setFormError('Please enter your email');
    return;
  }
  
  setIsLoading(true);
  setFormError('');
  
  try {
    const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: forgotPasswordEmail })
    });
    
    const data = await response.json();
    
    if (data.success) {
      setResetMessage('If an account exists with this email, a reset link has been sent.');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } else {
      setFormError(data.error || 'Failed to send reset link');
    }
  } catch (error) {
    setFormError('Connection error. Please try again.');
  } finally {
    setIsLoading(false);
  }
};

const handleResetPassword = async () => {
  if (!newPassword.trim()) {
    setFormError('Please enter a new password');
    return;
  }
  
  if (newPassword.length < 6) {
    setFormError('Password must be at least 6 characters');
    return;
  }
  
  setIsLoading(true);
  setFormError('');
  
  try {
    const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: resetToken, newPassword })
    });
    
    const data = await response.json();
    
    if (data.success) {
      setResetMessage('Password reset successfully! You can now login.');
      setShowResetPassword(false);
      setNewPassword('');
      setResetToken('');
    } else {
      setFormError(data.error || 'Failed to reset password');
    }
  } catch (error) {
    setFormError('Connection error. Please try again.');
  } finally {
    setIsLoading(false);
  }
};

const handleClientForgotPassword = async () => {
  if (!clientEmail.trim()) {
    setClientLoginError('Please enter your email');
    return;
  }
  
  setIsLoading(true);
  setClientLoginError('');
  
  try {
    const urlPath = window.location.pathname;
    const galleryMatch = urlPath.match(/\/client-gallery\/([a-zA-Z0-9]+)/);
    const galleryId = galleryMatch ? galleryMatch[1] : null;
    
    const response = await fetch(`${API_BASE}/api/client/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: clientEmail, galleryId })
    });
    
    const data = await response.json();
    
    if (data.success) {
      setClientLoginError('');
      alert('Your photographer has been notified and will send you a new password shortly.');
    } else {
      setClientLoginError(data.error || 'Failed to process request');
    }
  } catch (error) {
    setClientLoginError('Connection error. Please try again.');
  } finally {
    setIsLoading(false);
  }
};
useEffect(() => {
  // Check if URL has reset token
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  
  if (token) {
    setResetToken(token);
    setShowResetPassword(true);
  }
}, []);

  // Download functions - separate from selectGallery
// Download functions - separate from selectGallery
const downloadImage = async (imageId, filename) => {
  try {
    setIsDownloading(true);
    addToDebugLog(`Downloading: ${filename}`);
    
    // Use different endpoints for photographer vs client
    const url = currentUser.type === 'client'
      ? `${API_BASE}/api/client/download/${imageId}?accessToken=${currentUser.accessToken}`
      : `${API_BASE}/api/photographer/download/${imageId}`;
    
    const fetchOptions = currentUser.type === 'photographer' 
      ? { credentials: 'include' }
      : {};
    
    const response = await fetch(url, fetchOptions);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    addToDebugLog(`Download complete: ${filename}`);
  } catch (error) {
    addToDebugLog(`Download failed: ${error.message}`);
  } finally {
    setIsDownloading(false);
  }
};

const downloadSelectedImages = async () => {
  if (selectedPhotosForDownload.size === 0) return;
  
  try {
    setIsDownloading(true);
    const images = galleryData.images.filter(img => 
      selectedPhotosForDownload.has(img.id)
    );
    
    addToDebugLog(`Downloading ${images.length} images...`);
    
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      addToDebugLog(`Downloading ${i + 1}/${images.length}: ${image.filename}`);
      
      await downloadImage(image.id, image.filename);
      
      // Small delay between downloads to avoid browser blocking
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setSelectedPhotosForDownload(new Set());
    addToDebugLog('✓ All downloads complete!');
  } catch (error) {
    addToDebugLog(`Download error: ${error.message}`);
  } finally {
    setIsDownloading(false);
  }
};

const downloadAllImages = async () => {
  try {
    setIsDownloading(true);
    const regularImages = galleryData?.images?.filter(img => !img.isCover) || [];
    
    addToDebugLog(`Downloading all ${regularImages.length} images...`);
    
    for (let i = 0; i < regularImages.length; i++) {
      const image = regularImages[i];
      addToDebugLog(`Downloading ${i + 1}/${regularImages.length}: ${image.filename}`);
      
      await downloadImage(image.id, image.filename);
      
      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    addToDebugLog('✓ All downloads complete!');
  } catch (error) {
    addToDebugLog(`Download error: ${error.message}`);
  } finally {
    setIsDownloading(false);
  }
};

  const updateGalleryName = async (galleryId, newName) => {
    try {
      // Update local state immediately for UI responsiveness
      setLocalChanges(prev => ({
        ...prev,
        [galleryId]: {
          ...prev[galleryId],
          name: newName
        }
      }));
      
      if (selectedGallery?.id === galleryId) {
        setSelectedGallery(prev => ({ ...prev, name: newName }));
      }
      
      // Send to server to persist the change
      const response = await fetch(`${API_BASE}/api/galleries/${galleryId}/name`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newName })
      });

      const data = await response.json();
      if (data.success) {
        addToDebugLog(`Gallery name saved to database: ${newName}`);
        // Refresh galleries list to get updated data
        await loadGalleries();
      } else {
        addToDebugLog(`Failed to save gallery name: ${data.error}`);
        // Revert local changes if server update failed
        setLocalChanges(prev => {
          const updated = { ...prev };
          if (updated[galleryId]) {
            delete updated[galleryId].name;
          }
          return updated;
        });
      }
    } catch (error) {
      addToDebugLog(`Error updating gallery name: ${error.message}`);
      // Revert local changes if request failed
      setLocalChanges(prev => {
        const updated = { ...prev };
        if (updated[galleryId]) {
          delete updated[galleryId].name;
        }
        return updated;
      });
    }
  };

  const handleCoverPhotoUpload = async (event) => {
  const file = event.target.files[0];
  if (!file || !file.type.startsWith('image/') || !selectedGallery) {
    return;
  }

  try {
    addToDebugLog(`Uploading cover photo: ${file.name}`);
    
    // Upload to server
    const formData = new FormData();
    formData.append('coverPhoto', file);
    
    const response = await fetch(`${API_BASE}/api/galleries/${selectedGallery.id}/cover`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    
    const data = await response.json();
    
    if (data.success) {
      // Close the modal first
      setShowCoverUploadModal(false);
      
      // Reload the full gallery data to get the updated cover photo information
      const updatedGallery = await loadGallery(selectedGallery.id);
      if (updatedGallery) {
        setSelectedGallery(updatedGallery);
        addToDebugLog(`Cover photo uploaded successfully: ${data.coverPhotoId}`);
        
        // Also refresh the galleries list to show updated cover in gallery grid
        await loadGalleries();
      }
      
      // Clear any local preview state since we now have server data
      setCoverPhoto(null);
      setCoverPhotoFile(null);
      
    } else {
      addToDebugLog('Failed to save cover photo: ' + data.error);
      alert('Failed to upload cover photo: ' + data.error);
    }
    
  } catch (error) {
    console.error('Cover photo upload error:', error);
    addToDebugLog('Error uploading cover photo: ' + error.message);
    alert('Error uploading cover photo. Please try again.');
  }
  
  // Clear the file input regardless of success/failure
  event.target.value = '';
};

// ✅ SEPARATE FUNCTION - moved outside handleCoverPhotoUpload
const removeCoverPhoto = async () => {
  if (!selectedGallery) return;
  
  if (window.confirm('Remove this cover photo?')) {
    try {
      const response = await fetch(`${API_BASE}/api/galleries/${selectedGallery.id}/cover`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const updatedGallery = await loadGallery(selectedGallery.id);
        setSelectedGallery(updatedGallery);
        await loadGalleries();
        addToDebugLog('Cover photo removed');
      } else {
        addToDebugLog('Failed to remove cover photo');
      }
    } catch (error) {
      addToDebugLog('Failed to remove cover photo: ' + error.message);
    }
  }
};
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

const handleDrop = async (e, dropIndex) => {
  e.preventDefault();
  const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
  
  if (dragIndex === dropIndex || !selectedGallery?.images) return;
  
  // ✅ Work ONLY with non-cover images
  const regularImages = selectedGallery.images.filter(img => !img.isCover);
  const items = Array.from(regularImages);
  const [draggedItem] = items.splice(dragIndex, 1);
  items.splice(dropIndex, 0, draggedItem);
  
  const newOrder = items.map(photo => photo.id);
  
  // Update local state immediately for smooth UX
  setLocalChanges(prev => ({
    ...prev,
    [selectedGallery.id]: {
      ...prev[selectedGallery.id],
      imageOrder: newOrder
    }
  }));
  
  // ✅ Update gallery with cover photos + reordered regular photos
  const coverImages = selectedGallery.images.filter(img => img.isCover);
  setSelectedGallery(prev => ({
    ...prev,
    images: [...coverImages, ...items]
  }));
  
  addToDebugLog(`Moved photo from position ${dragIndex + 1} to ${dropIndex + 1}`);
  
async function fetchSignedUrl(key) {
  const res = await fetch(`${API_BASE}/api/r2-signed-url?key=${encodeURIComponent(key)}`, {
    credentials: "include",
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Failed to sign url");
  return data.url;
}


  // Save to server
  try {
    const response = await fetch(`${API_BASE}/api/galleries/${selectedGallery.id}/reorder`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ imageOrder: newOrder })
    });
    
    const data = await response.json();
    if (data.success) {
      addToDebugLog('Photo order saved to database');
    } else {
      addToDebugLog('Failed to save photo order: ' + data.error);
    }
  } catch (error) {
    addToDebugLog('Error saving photo order: ' + error.message);
  }
};

  const inviteClientToGallery = async () => {
    if (!selectedGallery) return;
    
    try {
      addToDebugLog('Sending invitation...');
      
      const response = await fetch(`${API_BASE}/api/galleries/${selectedGallery.id}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ sendEmail: true })
      });
      
      const data = await response.json();
      if (data.success) {
        addToDebugLog(`✓ Invitation prepared for ${selectedGallery.clientEmail}`);
        addToDebugLog(`Temporary password: ${data.details.temporaryPassword}`);
        addToDebugLog(`Gallery URL: ${data.details.galleryUrl}`);
        
        if (data.emailSent) {
          addToDebugLog(`✓ Email sent successfully via Elevate CRM!`);
          alert(`Invitation sent successfully!

Client: ${data.details.clientName}
Email: ${data.details.clientEmail}
Password: ${data.details.temporaryPassword}

Email was automatically sent via Elevate CRM using the standard template.
The client will receive gallery access instructions via email.`);
        } else if (data.emailError) {
          addToDebugLog(`✗ Email sending failed: ${data.emailError}`);
          alert(`Invitation prepared but email failed to send

Client: ${data.details.clientName}
Email: ${data.details.clientEmail}
Password: ${data.details.temporaryPassword}
Gallery URL: ${data.details.galleryUrl}

Email Error: ${data.emailError}

Please send the credentials manually or check your Elevate CRM configuration.`);
        } else {
          addToDebugLog(`Invitation prepared successfully`);
          alert(`Invitation prepared successfully!

Client: ${data.details.clientName}
Email: ${data.details.clientEmail}
Password: ${data.details.temporaryPassword}
Gallery URL: ${data.details.galleryUrl}

Email was sent automatically using the standard template.`);
        }
      } else {
        addToDebugLog('Failed to send invitation: ' + data.error);
        alert('Failed to prepare invitation: ' + data.error);
      }
    } catch (error) {
      addToDebugLog('Error sending invitation: ' + error.message);
      alert('Error preparing invitation: ' + error.message);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0 || !selectedGallery) return;
    
    const success = await uploadPhotos(selectedGallery.id, files);
    if (success) {
      setShowUploadModal(false);
      event.target.value = '';
    }
  };

  const handleClientLogin = async () => {
  if (!clientEmail.trim() || !galleryPassword.trim()) {
    setClientLoginError('Please enter both email and password');
    return;
  }

  setIsLoading(true);
  setClientLoginError('');
  
  try {
    addToDebugLog(`Attempting client login for ${clientEmail}`);
    
    const response = await fetch(`${API_BASE}/api/client/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      // ✅ DO NOT send credentials: 'include' for client login
      // Client login should NOT share photographer session
      body: JSON.stringify({
        email: clientEmail.trim(),
        password: galleryPassword
      })
    });

    const data = await response.json();

    if (data.success) {
      addToDebugLog(`Client login successful for ${clientEmail}`);
      
      // ✅ IMPORTANT: Clear any photographer session first
      // This prevents checkAuthStatus from overwriting
      setCurrentUser(null);
      
      // Small delay to ensure state updates properly
      setTimeout(() => {
        setCurrentUser({ 
          type: 'client',
          email: clientEmail,
          accessToken: data.accessToken 
        });
        setGalleryData(data.gallery);
        addToDebugLog(`Client user set with accessToken`);
      }, 0);
      
    } else {
      addToDebugLog(`Client login failed: ${data.error}`);
      setClientLoginError(data.error || 'Invalid credentials');
    }
  } catch (error) {
    console.error('Login error:', error);
    addToDebugLog(`Login error: ${error.message}`);
    setClientLoginError('Connection error. Please try again.');
  } finally {
    setIsLoading(false);
  }
};


  const logout = () => {
    setCurrentUser(null);
    setShowGalleryView(false);
    setShowUploadModal(false);
    setSelectedClient(null);
    setSelectedGallery(null);
    setSearchQuery('');
    setShowSettingsMenu(false);
    setShowBusinessSettings(false);
    setGalleries([]);
    setGalleryData(null);
    setClientEmail('');
    setGalleryPassword('');
    setClientLoginError('');
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          setBusinessSettings(prev => ({ ...prev, logo: reader.result }));
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Logo upload error:', error);
      }
    }
  };

  const filteredGalleries = galleries.filter(gallery => 
    gallery.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    gallery.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    gallery.clientEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
    gallery.sessionType.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getImageUrl = (key, size = 'preview') => {
  if (!key) return null;

  const encodedKey = encodeURIComponent(key);

  // ✅ Client gallery must use the client proxy + accessToken
  if (currentUser?.type === 'client') {
    const token = currentUser.accessToken;
    return `${API_BASE}/api/client/image-proxy/${encodedKey}?accessToken=${encodeURIComponent(token)}&size=${encodeURIComponent(size)}`;
  }

  // ✅ Photographer dashboard can use the authenticated proxy
  return `${API_BASE}/api/image-proxy/${encodedKey}?size=${encodeURIComponent(size)}`;
};

  const getCoverImage = () => {
    if (!galleryData?.images?.length) return null;
    const coverImage = galleryData.images.find(img => img.isCover) || galleryData.images[0];
    return coverImage ? getImageUrl(coverImage.previewId || coverImage.originalId) : null;
  };

  // ==================== LOGIN & VIEW RENDERING ====================

// LOGIN SCREEN - Show when no user is logged in
if (!currentUser) {
  const urlPath = window.location.pathname;
  const isClientLogin = urlPath.includes('/client');
  
  if (isClientLogin) {
  // CLIENT LOGIN SCREEN
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-4">
            {loadingBranding ? (
              <div className="h-20 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
              </div>
            ) : photographerBranding?.businessLogo ? (
              <img 
                src={photographerBranding.businessLogo} 
                alt="Business Logo" 
                className="h-20 w-auto mx-auto object-contain"
              />
            ) : (
              <img 
                src="elevatelogo.png" 
                alt="ELEVATE Logo" 
                className="h-20 w-auto mx-auto object-contain"
              />
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {photographerBranding?.businessName || 'Client Gallery Access'}
          </h1>
          <p className="text-gray-600">View your professional photos</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="your@email.com"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gallery Password</label>
            <input
              type="password"
              value={galleryPassword}
              onChange={(e) => setGalleryPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isLoading) {
                  handleClientLogin();
                }
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your gallery password"
              disabled={isLoading}
            />
          </div>

          <button
            onClick={handleClientLogin}
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-all disabled:opacity-50"
          >
            {isLoading ? 'Signing in...' : 'Access Gallery'}
          </button>

          {clientLoginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {clientLoginError}
            </div>
          )}

          <div className="text-center">
            <button
              type="button"
              onClick={handleClientForgotPassword}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Forgot your password?
            </button>
          </div>
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-700 mb-2 text-sm">Need help?</h4>
          <p className="text-xs text-gray-600">
            If you don't have your password, please contact your photographer.
          </p>
        </div>
      </div>
    </div>
  ); 

} else {
  // PHOTOGRAPHER LOGIN/SIGNUP SCREEN
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError('');

    try {
      const endpoint = isSignup ? '/api/auth/signup' : '/api/auth/login';
      
      const body = isSignup
        ? {
            email: formData.email,
            password: formData.password,
            ghlEmail: formData.ghlEmail,
            ghlLocationId: formData.ghlLocationId,
            ghlLocationToken: formData.ghlApiKey
          }
        : { 
            email: formData.email, 
            password: formData.password 
          };

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.success) {
        checkAuthStatus();
      } else {
        setFormError(data.error || 'Authentication failed');
      }
    } catch (error) {
      console.error('Auth error:', error);
      setFormError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src="elevatelogo.png" 
              alt="ELEVATE Logo" 
              className="h-20 w-auto mx-auto object-contain mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isSignup ? 'Create Account' : 'Photographer Login'}
            </h1>
            <p className="text-gray-600">ELEVATE Gallery Management</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {isSignup && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Elevate CRM Email
                  </label>
                  <input
                    type="email"
                    value={formData.ghlEmail}
                    onChange={(e) => setFormData({...formData, ghlEmail: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Elevate CRM login email"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location ID
                  </label>
                  <input
                    type="text"
                    value={formData.ghlLocationId}
                    onChange={(e) => setFormData({...formData, ghlLocationId: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Elevate CRM Location ID"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Find this in your Elevate CRM settings
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location Private Token
                  </label>
                  <input
                    type="password"
                    value={formData.ghlApiKey}
                    onChange={(e) => setFormData({...formData, ghlApiKey: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Elevate CRM Private Token"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Generate this in Elevate CRM: Settings → Private Integrations 
                  </p>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-all disabled:opacity-50"
            >
              {isLoading ? 'Please wait...' : (isSignup ? 'Create Account' : 'Sign In')}
            </button>

            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {formError}
              </div>
            )}

            {resetMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                {resetMessage}
              </div>
            )}
          </form>

          {!isSignup && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Forgot your password?
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsSignup(!isSignup);
                setFormError('');
              }}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
        </div>
      </div>

      {/* Forgot Password Modals */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Reset Password</h2>
              <button 
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail('');
                  setFormError('');
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            
            <input
              type="email"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
            />
            
            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                {formError}
              </div>
            )}
            
            <button
              onClick={handleForgotPassword}
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-all disabled:opacity-50"
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </div>
        </div>
      )}

      {showResetPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-xl">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Create New Password</h2>
            
            <p className="text-sm text-gray-600 mb-4">
              Enter your new password below.
            </p>
            
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 6 characters)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
            />
            
            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                {formError}
              </div>
            )}
            
            <button
              onClick={handleResetPassword}
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-all disabled:opacity-50"
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </div>
      )}
    </>
  );
  }
}
// CLIENT GALLERY WELCOME SCREEN - Show after client logs in
if (currentUser?.type === 'client' && !showGalleryView) {
  const coverImageUrl = getCoverImage();
  
  return (
    <div className="min-h-screen relative overflow-hidden">
      {coverImageUrl ? (
        <img 
          src={coverImageUrl} 
          alt="Cover" 
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => {
            console.error('Cover image load error:', e);
            addToDebugLog('Cover image failed to load');
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-gray-300"></div>
      )}
      <div className="absolute inset-0 bg-black bg-opacity-40"></div>
      
      <div className="relative z-10 min-h-screen flex items-center justify-center text-center text-white px-4">
        <div className="max-w-2xl">
          <h1 className="text-5xl md:text-7xl font-light tracking-wide mb-4 uppercase">
            {galleryData?.name || 'Your Gallery'}
          </h1>
          <p className="text-xl md:text-2xl font-light tracking-widest mb-12 opacity-90">
            {galleryData?.sessionType || 'Professional Photography Session'}
          </p>
          
          <button
  onClick={() => setShowGalleryView(true)}
  className="px-8 py-4 border-2 border-white text-white font-medium tracking-wider uppercase hover:bg-white hover:text-black transition-all duration-300 text-lg"
>
  View Gallery ({galleryData?.images?.filter(img => !img.isCover).length || 0} Photos)
</button>

<button 
  onClick={logout} 
  className="mt-4 px-4 py-2 text-white border border-white rounded hover:bg-white hover:text-black transition-all"
>
  Logout
</button>
        </div>
      </div>
    </div>
  );
}

// CLIENT GALLERY VIEW - Show client's photos
if (currentUser?.type === 'client' && showGalleryView) {
  const regularImages = galleryData?.images?.filter(img => !img.isCover) || [];
  
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white border-b border-gray-100 px-6 py-3">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg font-medium text-gray-800">
                {galleryData?.name || 'Client Gallery'}
              </h1>
              <p className="text-sm text-gray-600">
                {regularImages.length} Photos
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {selectedPhotosForDownload.size > 0 && (
                <button
                  onClick={downloadSelectedImages}
                  disabled={isDownloading}
                  className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 disabled:opacity-50"
                >
                  {isDownloading ? 'Downloading...' : `Download Selected (${selectedPhotosForDownload.size})`}
                </button>
              )}
              <button
                onClick={downloadAllImages}
                disabled={isDownloading}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
              >
                {isDownloading ? 'Downloading...' : 'Download All'}
              </button>
              <button 
                onClick={() => setShowGalleryView(false)} 
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Back
              </button>
              <button 
                onClick={logout} 
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        <div className="p-8 bg-gray-50 min-h-screen">
          {!regularImages.length ? (
            <div className="text-center py-16 text-gray-500 bg-white rounded-lg shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No photos available</h3>
              <p className="text-gray-500">This gallery is empty or still being prepared.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {regularImages.map((image, index) => {
                const thumbnailUrl = getImageUrl(image.thumbnailId || image.previewId || image.originalId, 'thumbnail');
                
                return (
                  <div
                    key={image.id}
                    className="group relative aspect-square bg-white rounded-md overflow-hidden shadow-sm transition-all duration-300 hover:shadow-lg hover:scale-105"
                  >
                    <img 
                      src={thumbnailUrl} 
                      alt={image.filename} 
                      className="w-full h-full object-cover cursor-pointer"
                      loading="lazy"
                      onClick={() => {
                        setCurrentSlideIndex(index);
                        setShowSlideshow(true);
                      }}
                      onError={(e) => {
                        console.error(`Image load error for ${image.filename}:`, e);
                        addToDebugLog(`Failed to load image: ${image.filename}`);
                      }}
                    />
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newSelection = new Set(selectedPhotosForDownload);
                        if (newSelection.has(image.id)) {
                          newSelection.delete(image.id);
                        } else {
                          newSelection.add(image.id);
                        }
                        setSelectedPhotosForDownload(newSelection);
                      }}
                      className={`absolute top-2 left-2 w-6 h-6 rounded border-2 border-white flex items-center justify-center transition-all z-10 ${
                        selectedPhotosForDownload.has(image.id) 
                          ? 'bg-blue-600' 
                          : 'bg-black bg-opacity-30 hover:bg-opacity-50'
                      }`}
                    >
                      {selectedPhotosForDownload.has(image.id) && (
                        <span className="text-white text-sm font-bold">✓</span>
                      )}
                    </button>
                    
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 pointer-events-none"></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showSlideshow && regularImages.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
            <button 
              onClick={() => setShowSlideshow(false)} 
              className="absolute top-6 right-6 text-white text-2xl hover:text-gray-300 z-10"
            >
              ×
            </button>
            <button 
              onClick={() => setCurrentSlideIndex(prev => 
                prev > 0 ? prev - 1 : regularImages.length - 1
              )} 
              className="absolute left-6 text-white text-3xl hover:text-gray-300 z-10"
            >
              ←
            </button>
            <button 
              onClick={() => setCurrentSlideIndex(prev => 
                prev < regularImages.length - 1 ? prev + 1 : 0
              )} 
              className="absolute right-6 text-white text-3xl hover:text-gray-300 z-10"
            >
              →
            </button>
            
            {regularImages[currentSlideIndex] && (
              <img 
                src={getImageUrl(
                  regularImages[currentSlideIndex].previewId || 
                  regularImages[currentSlideIndex].originalId, 
                  'preview'
                )} 
                alt={regularImages[currentSlideIndex].filename} 
                className="max-w-[90vw] max-h-[90vh] object-contain"
                onError={(e) => {
                  console.error('Slideshow image load error:', e);
                  addToDebugLog('Slideshow image failed to load');
                }}
              />
            )}
            
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white">
              {currentSlideIndex + 1} / {regularImages.length}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Continue with BUSINESS SETTINGS and PHOTOGRAPHER DASHBOARD below...
  // BUSINESS SETTINGS
  if (currentUser.type === 'photographer' && showBusinessSettings) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border-b border-gray-100 px-6 py-4">
            <div className="flex items-center">
              <button onClick={() => setShowBusinessSettings(false)} className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                ←
              </button>
              <div>
                <h1 className="text-xl font-medium text-gray-800">Business Settings</h1>
                <p className="text-sm text-gray-600">Configure your business information</p>
              </div>
            </div>
          </div>

          <div className="p-8 bg-gray-50 min-h-screen">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-medium text-gray-900">Business Information</h2>
                <p className="text-sm text-gray-500 mt-1">This information will be displayed to your clients</p>
              </div>
              
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Logo</label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {businessSettings.logo ? (
                        <img src={businessSettings.logo} alt="Business Logo" className="h-16 w-16 object-contain border border-gray-300 rounded" />
                      ) : (
                        <div className="h-16 w-16 bg-gray-100 border border-gray-300 rounded flex items-center justify-center">
                          <span className="text-gray-400 text-xs">No Logo</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB. Will appear on login screen.</p>
                    </div>
                  </div>
                </div>

              

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location ID</label>
                  <input
                    type="text"
                    value={businessSettings.ghlLocationId}
                    onChange={(e) => setBusinessSettings(prev => ({ ...prev, ghlLocationId: e.target.value }))}
                    className="w-full max-w-md p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your Elevate CRM Location ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Name
                    {loadingLocationName && <span className="text-xs text-blue-600 ml-2">(Loading from CRM...)</span>}
                  </label>
                  <input
                    type="text"
                    value={businessSettings.businessName}
                    onChange={(e) => setBusinessSettings(prev => ({ ...prev, businessName: e.target.value }))}
                    className="w-full max-w-md p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your business name"
                    disabled={loadingLocationName}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Write the name you wish to display on the Gallery.
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <button
                    onClick={saveBusinessSettings}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Save Settings
                  </button>
                  
                  {saveMessage && (
                    <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      saveMessage === 'Changes Saved' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {saveMessage}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // MAIN PHOTOGRAPHER DASHBOARD
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white border-b border-gray-100 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-medium text-gray-800">{businessSettings.businessName || 'Photography Gallery'}</h1>
              <p className="text-sm text-gray-600">Professional Gallery Management</p>
            </div>
            <div className="flex items-center space-x-4">
              <input
                type="text"
                placeholder="Search galleries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              
              <div className="relative">
                <button 
                  onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
                
                {showSettingsMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowBusinessSettings(true);
                          setShowSettingsMenu(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Business Settings
                      </button>
                      <button
                        onClick={() => {
                          setShowSettingsMenu(false);
                          logout();
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-8 bg-gray-50 min-h-screen">
          {selectedGallery ? (
            <div>
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <button onClick={() => setSelectedGallery(null)} className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">← Back</button>
                  <div>
                    {editingGalleryName ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={tempGalleryName}
                          onChange={(e) => setTempGalleryName(e.target.value)}
                          className="text-xl font-medium text-gray-800 bg-transparent border-b border-blue-500 focus:outline-none"
                          onBlur={() => {
                            updateGalleryName(selectedGallery.id, tempGalleryName);
                            setEditingGalleryName(false);
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              updateGalleryName(selectedGallery.id, tempGalleryName);
                              setEditingGalleryName(false);
                            }
                          }}
                          autoFocus
                        />
                      </div>
                    ) : (
                      <h1 
                        className="text-xl font-medium text-gray-800 cursor-pointer hover:text-blue-600"
                        onClick={() => {
                          setEditingGalleryName(true);
                          setTempGalleryName(selectedGallery?.name || '');
                        }}
                      >
                        {selectedGallery?.name || 'Gallery'} ✎
                      </h1>
                    )}
                    <p className="text-sm text-gray-600">{selectedGallery?.sessionType} • {selectedGallery?.images?.filter(img => !img.isCover).length || 0} photos</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {selectedPhotos.size > 0 && (
                    <div className="flex items-center space-x-2 bg-red-50 px-4 py-2 rounded-lg">
                      <span className="text-sm text-red-700">{selectedPhotos.size} selected</span>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete ${selectedPhotos.size} selected photos?`)) {
                            deleteSelectedPhotos();
                          }
                        }}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setSelectedPhotos(new Set())}
                        className="text-gray-600 hover:text-gray-800 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  
                  <button 
                    onClick={inviteClientToGallery}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors relative group"
                  >
                    <img src="invite2.png" alt="Invite Client" width="30" height="30" />
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                      Invite Client
                    </div>
                  </button>
                  
                  <button 
                    onClick={() => setShowUploadModal(true)}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors relative group"
                  >
                    <img src="upload2.png" alt="Upload Photos" width="30" height="30" />
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                      Upload Photos
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this entire gallery? This cannot be undone.')) {
                        deleteGallery(selectedGallery.id);
                      }
                    }}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors relative group"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3,6 5,6 21,6"/>
                      <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"/>
                      <line x1="10" y1="11" x2="10" y2="17"/>
                      <line x1="14" y1="11" x2="14" y2="17"/>
                    </svg>
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                      Delete Gallery
                    </div>
                  </button>
                </div>
              </div>

              {(selectedGallery?.images?.filter(img => !img.isCover).length || 0) === 0 ? (
                <div className="text-center py-16 text-gray-500 bg-white rounded-lg shadow-sm">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No photos uploaded yet</h3>
                  <p className="text-gray-500 mb-6">Get started by uploading photos for {selectedGallery?.clientName}</p>
                  <button onClick={() => setShowUploadModal(true)} className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800">Upload First Photos</button>
                </div>
              ) : (
                <div className="space-y-6">
                 {/* Cover Photo Section */}
<div className="bg-white rounded-lg shadow-sm overflow-hidden">
  <div className="px-6 py-4 border-b border-gray-100">
    <h2 className="text-lg font-medium text-gray-900">Cover Photo</h2>
    <p className="text-sm text-gray-500 mt-1">Drag a photo from below or upload a custom image</p>
  </div>
  <div className="p-6">
    <div className="flex items-start space-x-6">
      <div 
        className="flex-shrink-0"
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.classList.add('ring-2', 'ring-blue-500');
        }}
        onDragLeave={(e) => {
          e.currentTarget.classList.remove('ring-2', 'ring-blue-500');
        }}
        onDrop={async (e) => {
  e.preventDefault();
  e.currentTarget.classList.remove('ring-2', 'ring-blue-500');
  
  const photoId = e.dataTransfer.getData('photoId');
  if (!photoId) return;
  
  try {
    addToDebugLog(`Setting photo ${photoId} as cover...`);
    
    // Use PATCH to update which photo is the cover
    const response = await fetch(`${API_BASE}/api/galleries/${selectedGallery.id}/cover-photo`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ imageId: photoId })
    });
    
    const data = await response.json();
    
    if (data.success) {
      const updatedGallery = await loadGallery(selectedGallery.id);
      setSelectedGallery(updatedGallery);
      await loadGalleries();
      addToDebugLog('Cover photo updated successfully');
    } else {
      addToDebugLog('Failed to set cover photo: ' + data.error);
      alert('Failed to set cover photo: ' + data.error);
    }
  } catch (error) {
    addToDebugLog('Error setting cover photo: ' + error.message);
    alert('Error setting cover photo: ' + error.message);
  }
}}
      >
        {(() => {
          const coverImage = selectedGallery?.images?.find(img => img.isCover);
          return coverImage ? (
            <div className="relative">
              <img 
                src={getImageUrl(coverImage.thumbnailId || coverImage.previewId, 'thumbnail')}
                alt="Cover photo"
                className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200"
              />
              <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                COVER
              </div>
              <button
                onClick={removeCoverPhoto}
                className="absolute -top-2 -left-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
              >
                ×
              </button>
            </div>
          ) : (
            <div className="w-32 h-32 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
              <div className="text-center">
                <div className="text-gray-400 text-2xl mb-1">📷</div>
                <p className="text-xs text-gray-500">Drop photo here</p>
              </div>
            </div>
          );
        })()}
      </div>
      <div className="flex-1">
        <h3 className="font-medium text-gray-900 mb-2">Gallery Cover Image</h3>
        <p className="text-sm text-gray-600 mb-4">
          Drag a photo from below or upload a custom cover photo. This can be any image - it doesn't need to be from the gallery photos.
        </p>
        <button
          onClick={() => setShowCoverUploadModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
        >
          {coverPhoto ? 'Change Cover Photo' : 'Upload Cover Photo'}
        </button>
      </div>
    </div>
  </div>
</div>

                  {/* Gallery Photos Section */}
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100">
                      <h2 className="text-lg font-medium text-gray-900">Gallery Photos ({selectedGallery?.images?.filter(img => !img.isCover).length || 0})</h2>
                      <p className="text-sm text-gray-500 mt-1">Drag to reorder • Click to select for deletion</p>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                        {selectedGallery.images.filter(img => !img.isCover).map((photo, index) => (
                          <div
                            key={photo.id}
                            draggable
                            onDragStart={(e) => {
  handleDragStart(e, index);
  e.dataTransfer.setData('photoId', photo.id);
}}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index)}
                            className={`relative rounded-lg overflow-hidden transition-all group aspect-square cursor-move ${
                              selectedPhotos.has(photo.id) 
                                ? 'border-2 border-blue-500 ring-2 ring-blue-200' 
                                : 'border-2 border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <img 
                              src={`${API_BASE}/api/image-proxy/${photo.thumbnailId}`} 
                              alt={photo.filename} 
                              className="w-full h-full object-cover"
                              draggable={false}
                            />

                            <button
                              type="button"
                              onClick={() => {
                                const newSelection = new Set(selectedPhotos);
                                if (newSelection.has(photo.id)) {
                                  newSelection.delete(photo.id);
                                } else {
                                  newSelection.add(photo.id);
                                }
                                setSelectedPhotos(newSelection);
                              }}
                              className="absolute top-2 left-2 w-6 h-6 rounded border-2 border-white bg-black bg-opacity-50 hover:bg-opacity-70 flex items-center justify-center transition-all"
                            >
                              {selectedPhotos.has(photo.id) && (
                                <span className="text-white text-sm font-bold">✓</span>
                              )}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cover Photo Upload Modal */}
              {showCoverUploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-lg p-8 max-w-lg w-full shadow-xl">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold text-gray-900">Upload Cover Photo</h2>
                      <button onClick={() => setShowCoverUploadModal(false)} className="text-gray-400 hover:text-gray-600">×</button>
                    </div>
                    
                    <div className="mb-6">
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCoverPhotoUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                          <div className="text-gray-400 text-4xl mb-4">📷</div>
                          <p className="text-gray-600 font-medium mb-2">
                            Click to select cover photo
                          </p>
                          <p className="text-sm text-gray-500">JPG, PNG, WebP up to 10MB</p>
                          <p className="text-xs text-gray-400 mt-2">This image will represent your gallery and doesn't need to be from the gallery photos</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showUploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-lg p-8 max-w-lg w-full shadow-xl">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold text-gray-900">Upload Photos</h2>
                      <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">×</button>
                    </div>
                    
                    <div className="mb-6">
                      <div className="relative">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleFileUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          disabled={uploadProgress}
                        />
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
  {uploadProgress ? (
    <div className="flex flex-col items-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
      <p className="text-gray-600 font-medium mb-2">Uploading your images...</p>
      <p className="text-sm text-gray-500">This may take a few moments</p>
    </div>
  ) : (
    <>
      <p className="text-gray-600 font-medium mb-2">
        Drop unlimited photos here or click to browse
      </p>
      <p className="text-sm text-gray-500">Supported formats: JPG, PNG, WebP, GIF</p>
      <p className="text-xs text-gray-400 mt-2">Select as many photos as you want - no limit!</p>
    </>
  )}
</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="mb-8 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                    Client Galleries {searchQuery && `(${filteredGalleries.length} results)`}
                  </h2>
                  <p className="text-gray-600">Manage photo galleries for your clients</p>
                </div>
                <button
                  onClick={() => {
                    setShowAddClientModal(true);
                    fetchGHLContacts();
                  }}
                  className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
                >
                  <span>+</span>
                  <span>Create New Gallery</span>
                </button>
              </div>

              {loading ? (
                <div className="text-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading galleries...</p>
                </div>
              ) : filteredGalleries.length === 0 ? (
                <div className="text-center py-16 text-gray-500 bg-white rounded-lg shadow-sm">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No galleries found</h3>
                  <p className="text-gray-500 mb-6">Create your first gallery to get started</p>
                  <button 
                    onClick={() => {
                      setShowAddClientModal(true);
                      fetchGHLContacts();
                    }}
                    className="bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800"
                  >
                    Create First Gallery
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredGalleries.map((gallery) => {
                    const galleryWithChanges = applyLocalChanges(gallery);
                    const regularImages = galleryWithChanges.images?.filter(img => !img.isCover) || [];
                    const totalPhotos = regularImages.length;
                    const coverPhoto = galleryWithChanges.images?.find(img => img.isCover) || galleryWithChanges.images?.[0];
                    
                    return (
                      <div
                        key={gallery.id}
                        onClick={() => selectGallery(gallery)}
                        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-all duration-200 hover:scale-105"
                      >
                        <div className="aspect-video bg-gray-100 relative overflow-hidden">
                          {coverPhoto ? (
                            <img
  src={getImageUrl(coverPhoto.thumbnailId || coverPhoto.previewId || coverPhoto.originalId, 'thumbnail')}
  alt={`${galleryWithChanges.name} cover`}
  className="w-full h-full object-cover"
/>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <div className="text-center">
                                <div className="text-4xl mb-2">📷</div>
                                <p className="text-sm">No photos</p>
                              </div>
                            </div>
                          )}
                          {totalPhotos > 0 && (
                            <div className="absolute top-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
                              {totalPhotos} {totalPhotos === 1 ? 'photo' : 'photos'}
                            </div>
                          )}
                        </div>

                        <div className="p-4">
                          <h3 className="font-semibold text-gray-900 mb-1">{galleryWithChanges.name}</h3>
                          <p className="text-sm text-gray-600 mb-2">{gallery.sessionType}</p>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">{new Date(gallery.sessionDate).toLocaleDateString()}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                {gallery.clientName}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

{showAddClientModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-xl relative">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Create New Gallery</h2>
        <button 
          onClick={() => !creatingGallery && setShowAddClientModal(false)} 
          disabled={creatingGallery}
          className={`text-gray-400 hover:text-gray-600 text-2xl ${creatingGallery ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          ×
        </button>
      </div>

      {/* Loading Overlay */}
      {creatingGallery && (
        <div className="absolute inset-0 bg-white bg-opacity-95 flex flex-col items-center justify-center z-10 rounded-lg">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
          <p className="text-lg font-semibold text-gray-700">Creating Gallery...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait, this may take a moment</p>
        </div>
      )}

      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900 mb-3">Search Elevate CRM Contacts</h3>
        <div className="relative">
          <input
            type="text"
            value={contactSearchQuery}
            onChange={(e) => {
              setContactSearchQuery(e.target.value);
              if (e.target.value.length >= 2) {
                fetchGHLContacts(e.target.value);
              } else {
                setGhlContacts([]);
              }
            }}
            placeholder="Type at least 2 characters to search contacts..."
            disabled={creatingGallery}
            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              creatingGallery ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          />
          {searchingContacts && (
            <div className="absolute right-3 top-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          💡 Start typing a name, email, or phone number to find contacts
        </p>
      </div>

      {!ghlConfigured ? (
        <div className="text-center py-8">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Connect Elevate CRM</h3>
          <p className="text-gray-500 mb-4">Enter your Elevate CRM Location ID to import contacts</p>
          <button
            onClick={() => {
              setShowAddClientModal(false);
              setShowBusinessSettings(true);
            }}
            disabled={creatingGallery}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            Configure Elevate CRM
          </button>
        </div>
      ) : contactSearchQuery.length < 2 ? (
        <div className="text-center py-12 text-gray-500">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-lg">Search for contacts above</p>
          <p className="text-sm mt-1">Enter at least 2 characters to begin searching</p>
        </div>
      ) : searchingContacts ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Searching contacts...</p>
        </div>
      ) : ghlContacts.length === 0 ? (
        <div className="text-center py-8">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Contacts Found</h3>
          <p className="text-gray-500 mb-4">
            No contacts match "{contactSearchQuery}". Try a different search term.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 mb-2">
            Found {ghlContacts.length} contact{ghlContacts.length !== 1 ? 's' : ''}
          </p>
          {ghlContacts.map((contact) => {
            const existingGallery = galleries.find(g => g.ghlContactId === contact.id);
            const defaultGalleryName = `${contact.firstName} ${contact.lastName}`;
            return (
              <div
                key={contact.id}
                className={`p-4 border rounded-lg transition-colors ${
                  existingGallery 
                    ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60' 
                    : creatingGallery
                    ? 'bg-white border-gray-200 opacity-50 cursor-not-allowed'
                    : 'bg-white border-gray-200 hover:border-blue-300 cursor-pointer hover:bg-blue-50'
                }`}
                onClick={() => !existingGallery && !creatingGallery && createGalleryFromContact(contact)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-gray-900">{defaultGalleryName}</h4>
                    <p className="text-sm text-gray-600">{contact.email}</p>
                    {contact.phone && <p className="text-sm text-gray-500">{contact.phone}</p>}
                    <p className="text-xs text-gray-400 mt-1">Gallery will be named: "{defaultGalleryName}"</p>
                  </div>
                  <div>
                    {existingGallery ? (
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">Gallery Exists</span>
                    ) : (
                      <button 
                        disabled={creatingGallery}
                        className={`text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors ${
                          creatingGallery ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {creatingGallery ? 'Creating...' : 'Create Gallery'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default PhotoGalleryApp;
